import { useState, useEffect } from 'react';

const JSON_SOURCES = [
  '/dekton-slabs.json',
  '/caesarstone-slabs.json',
  '/sensa-slabs.json',
  '/scalea-slabs.json',
  '/atlasplan-slabs.json',
  '/solid-slabs.json',
];

/** Normalize for fuzzy matching: lowercase, strip spaces/hyphens/special chars */
const norm = (str) =>
  String(str).toLowerCase().replace(/[\s\-_]/g, '').replace(/[^a-z0-9]/g, '');

// Module-level cache — only fetched once per session
let cachedMap = null;
let fetchPromise = null;

const loadAllSlabs = () => {
  if (cachedMap) return Promise.resolve(cachedMap);
  if (fetchPromise) return fetchPromise;

  fetchPromise = Promise.all(JSON_SOURCES.map(f => fetch(f).then(r => r.json())))
    .then(results => {
      const map = {};
      results.flat().forEach(item => {
        const nameKey = norm(item.name);
        if (nameKey) map[nameKey] = item;
        // Also index by slug for additional coverage
        if (item.slug) {
          const slugKey = norm(item.slug);
          if (slugKey && !map[slugKey]) map[slugKey] = item;
        }
      });
      cachedMap = map;
      return map;
    })
    .catch(() => {
      cachedMap = {};
      return {};
    });

  return fetchPromise;
};

export const useSlabData = () => {
  const [slabMap, setSlabMap] = useState(cachedMap);

  useEffect(() => {
    if (!cachedMap) {
      loadAllSlabs().then(map => setSlabMap(map));
    }
  }, []);

  /**
   * Match an overlay path to a JSON slab entry.
   *
   * Overlay paths from Sidebar look like:
   *   /Dekton - Bergen.webp
   *   /Caesarstone - 5133 - SymphonyGrey.webp
   *   /Porcelain - Calacatta Extra Polished 12mm.webp
   *   /Granite - Naori.webp
   *
   * Matching tries, in order:
   *   1. Exact normalized match on trailing segment(s)
   *   2. Last segment is a prefix match of a JSON name (handles "…12mm" suffix)
   *   3. Full filename contains a JSON name (last resort)
   */
  const findSlab = (overlayPath) => {
    if (!slabMap || !overlayPath) return null;

    const filename = overlayPath.split('/').pop().replace(/\.[^/.]+$/, '');
    const parts = filename.split(' - ');

    // Strategy 1: exact match on trailing part combinations (right → left)
    for (let i = parts.length - 1; i >= 0; i--) {
      const candidate = norm(parts.slice(i).join(' '));
      if (candidate && slabMap[candidate]) return slabMap[candidate];
    }

    const lastNorm = norm(parts[parts.length - 1]);
    const allEntries = Object.values(slabMap);

    // Strategy 2: JSON name is a prefix of the last segment (e.g. "Calacatta Extra Polished" ⊂ "Calacatta Extra Polished 12mm")
    const prefixMatch = allEntries.find(e => {
      const eNorm = norm(e.name);
      return eNorm.length >= 5 && lastNorm.startsWith(eNorm);
    });
    if (prefixMatch) return prefixMatch;

    // Strategy 3: full filename contains a JSON name
    const fullNorm = norm(filename);
    return allEntries.find(e => {
      const eNorm = norm(e.name);
      return eNorm.length >= 5 && fullNorm.includes(eNorm);
    }) || null;
  };

  return { findSlab, isLoaded: !!slabMap };
};
